// FSSAI Central License.jsx
import React from "react";

export default function FSSAICentralLicense() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            FSSAI Central License
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            FSSAI Central License for Large-Scale Food Businesses
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks supports food businesses in obtaining an FSSAI
            Central License for large-scale operations, imports/exports, and
            businesses operating across multiple states, ensuring they stay
            compliant with national-level regulatory standards.
          </p>
          <p className="text-gray-700">
            From eligibility assessment and documentation planning to application
            filing and coordinating with the Central Licensing Authority, the
            process is handled in a structured, compliance-driven manner so that
            your food business remains audit-ready and approval-focused.
          </p>
        </div>

        {/* Right: Image (FSSAI CENTRAL LICENSE - Large scale food manufacturing/import-export compliance) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/461198/pexels-photo-461198.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Large scale food manufacturing plant FSSAI Central License compliance"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks offers end-to-end support for FSSAI Central
              License applications for food businesses engaged in large-scale
              manufacturing, imports/exports, or multi-state operations.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Eligibility mapping for Central License based on scale,
                  turnover, multi-state presence, and import/export activity.
                </li>
                <li>
                  Drafting, reviewing, and filing Central License applications
                  in line with FSSAI norms and documentation standards.
                </li>
                <li>
                  Coordinating with Central Licensing Authority for
                  clarifications, queries, and inspection-linked requirements.
                </li>
                <li>
                  Ongoing advisory on renewal timelines and post-license
                  compliance to avoid interruptions and penalties.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for FSSAI Central License
            </h2>
            <p className="text-gray-700 mb-4">
              The team assists in collating and structuring the core business
              and statutory documents necessary for a Central License
              application under FSSAI regulations.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>PAN card of the company/firm and key persons.</li>
                <li>
                  Company incorporation documents such as Certificate of
                  Incorporation, partnership deed, or LLP agreement.
                </li>
                <li>
                  Valid Import Export Code (IEC) for businesses involved in
                  import or export of food products.
                </li>
                <li>
                  Food safety compliance records, including prior licenses,
                  internal audit reports, or basic food safety systems in place.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm combines deep FSSAI regulatory understanding with a
              process-driven approach to Central Licensing, reducing the risk of
              rejections and delays.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  In-Depth FSSAI Knowledge
                </h3>
                <p className="text-gray-700 text-sm">
                  Specialist understanding of Central License requirements for
                  large, multi-state, and import/export-oriented food
                  businesses, aligned with current FSSAI regulations.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate &amp; Timely Filing
                </h3>
                <p className="text-gray-700 text-sm">
                  Structured documentation and filing workflows designed to
                  minimise errors, address likely queries, and support timely
                  grant of license.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Compliance Management
                </h3>
                <p className="text-gray-700 text-sm">
                  Ongoing advisory on renewals, amendments, and compliance
                  obligations under the Food Safety and Standards Act to keep
                  your Central License in good standing.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Start Your Central License Application
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to evaluate your eligibility,
                understand document requirements, and initiate a compliant
                Central License application for your food business.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
